import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useScrollPosition } from '@/hooks/useScrollPosition'
import { Recipe } from '@/types/recipe'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'


import UserManagementV2 from '@/components/UserManagementV2'
import ExportModal from '@/components/ExportModal'
import RecipeFilterToolbar from '@/components/RecipeFilterToolbar'
import { useRecipeStore } from '@/stores/recipeStore'
import { useAuth } from '@/contexts/AuthContext'
import { getAllRecipes } from '@/lib/supabase-service'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'


import DashboardOverview from '@/components/dashboard/DashboardOverview';
import EnhancedUserTable from '@/components/dashboard/EnhancedUserTable';
import { 
  getDashboardStats, 
  getDashboardUsers, 
  exportUserData,
  type DashboardStats,
  type DashboardUser 
} from '@/lib/dashboard-service';
import ManageUserDialog from '@/components/dashboard/ManageUserDialog';
import { supabase } from '@/lib/supabaseClient';
import { deleteUser } from '@/lib/user-service';
import {
  Edit2, Trash2, Star, Users, Eye, EyeOff,
  ChevronUp, ChevronDown, Filter,
  LayoutDashboard, BookOpen, Cpu
} from 'lucide-react';

interface ManagePageProps {
  recipes: Recipe[]
  categories: readonly string[]
  onEdit: (recipe: Recipe) => void
  onDelete: (id: string) => void
  onImportRecipe: (formData: any) => Promise<void>
  isAdmin: boolean
}

export default function ManagePage({ onEdit, onDelete, onImportRecipe: _onImportRecipe, isAdmin }: ManagePageProps) {
  const { user, role } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { saveScrollPosition } = useScrollPosition()

  // --- Dashboard state ---
  const [dashboardView, setDashboardView] = useState<'dashboard' | 'users' | 'recipes'>('dashboard');

  // --- Sync dashboardView with tab param ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'dashboard' || tab === 'users' || tab === 'recipes') {
      setDashboardView(tab);
    } else {
      setDashboardView('dashboard');
    }
  }, [location.search]);

  // Check if user has access (admin or reader) - but don't early return
  const hasAccess = isAdmin || role === 'reader';
  
  // Use store directly instead of props for recipes
  const {
    recipes: storeRecipes,
    loading,
    filters,
    pagination,
    setSearchQuery,
    setCategory,
    setShowFavoritesOnly,
    setShowOwnRecipesOnly,
    setLimit,
    nextPage,
    previousPage,
    toggleFavorite,
    toggleShare
  } = useRecipeStore()

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 640);
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([])
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportMode, setExportMode] = useState<'selected' | 'all'>('selected')
  const [allRecipesForExport, setAllRecipesForExport] = useState<Recipe[]>([])
  const [loadingAllRecipes, setLoadingAllRecipes] = useState(false)
  const [showUserManagement, setShowUserManagement] = useState(false)
  const [favoriteLoading, setFavoriteLoading] = useState<string | null>(null)
  const [shareLoading, setShareLoading] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Recipe | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })

  // ADDED: Dashboard state
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [dashboardUsers, setDashboardUsers] = useState<DashboardUser[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSubscriptionFilter, setUserSubscriptionFilter] = useState('all');
  const [userActivityFilter, setUserActivityFilter] = useState('all');
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userTotalCount, setUserTotalCount] = useState(0);

  // --- Admin Manage User Dialog State ---
  const [manageUserDialogOpen, setManageUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DashboardUser | null>(null);
  const [manageUserLoading, setManageUserLoading] = useState(false);
  // Track last dashboard view for modal routing
  const [lastDashboardView, setLastDashboardView] = useState<'dashboard' | 'users' | 'recipes'>('dashboard');

  // Load recipes and set to show 25 by default
  useEffect(() => {
    if (user && hasAccess) {
      setLimit(25); // Show 25 recipes by default in management view
    }
  }, [user, hasAccess, setLimit]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fix scrollbar layout shift issue
  useEffect(() => {
    // Force body to always show scrollbar
    document.body.style.overflowY = 'scroll';
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflowY = '';
    };
  }, []);

  // Filter recipes for ManagePage: admins see all, readers see only their own
  const recipes = isAdmin ? storeRecipes : storeRecipes.filter(recipe => recipe.isOwner);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRecipes(filteredRecipes.map(recipe => recipe.id))
    } else {
      setSelectedRecipes([])
    }
  }

  const handleSelectRecipe = (recipeId: string, checked: boolean) => {
    if (checked) {
      setSelectedRecipes([...selectedRecipes, recipeId])
    } else {
      setSelectedRecipes(selectedRecipes.filter(id => id !== recipeId))
    }
  }

  const handleExportSelected = () => {
    if (selectedRecipes.length > 0) {
      setExportMode('selected')
      setShowExportModal(true)
    }
  }

  const handleExportAll = async () => {
    setLoadingAllRecipes(true)
    try {
      // Fetch ALL recipes directly from database, bypassing any filters or pagination
      const allRecipes = await getAllRecipes()
      // Filter for readers: they should only export their own recipes
      const recipesToExport = isAdmin ? allRecipes : allRecipes.filter(recipe => recipe.isOwner)
      setAllRecipesForExport(recipesToExport)
      setExportMode('all')
      setShowExportModal(true)
    } catch {
    } finally {
      setLoadingAllRecipes(false)
    }
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Navigation handler for recipe clicks
  const handleRecipeClick = (recipe: Recipe) => {
    // Save current scroll position for the manage page
    saveScrollPosition(location.pathname);
    navigate(`/recipe/${recipe.id}`);
  };

  // Enhanced favorite handler with loading state
  const handleFavoriteClick = async (recipe: Recipe) => {
    setFavoriteLoading(recipe.id);
    try {
      await toggleFavorite(recipe.id);
    } catch {
    } finally {
      setFavoriteLoading(null);
    }
  };

  // Share toggle handler
  const handleShareToggle = async (recipe: Recipe) => {
    setShareLoading(recipe.id);
    try {
      await toggleShare(recipe.id, recipe.share);
    } catch {
    } finally {
      setShareLoading(null);
    }
  };

  const handleSort = (key: keyof Recipe) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedRecipes = (recipes: Recipe[]) => {
    if (!sortConfig.key) return recipes;

    return [...recipes].sort((a, b) => {
      const aVal = a[sortConfig.key!];
      const bVal = b[sortConfig.key!];

      // Handle different data types
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const result = aVal === bVal ? 0 : aVal ? 1 : -1;
        return sortConfig.direction === 'asc' ? result : -result;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      // Handle dates
      if (sortConfig.key === 'dateCreated' || sortConfig.key === 'dateModified') {
        const dateA = new Date(aVal as string).getTime();
        const dateB = new Date(bVal as string).getTime();
        const result = dateA - dateB;
        return sortConfig.direction === 'asc' ? result : -result;
      }

      return 0;
    });
  };

  const SortableHeader = ({
    children,
    sortKey,
    className = ""
  }: {
    children: React.ReactNode;
    sortKey: keyof Recipe;
    className?: string;
  }) => {
    const isActive = sortConfig.key === sortKey;
    const isAsc = isActive && sortConfig.direction === 'asc';

    return (
      <TableHead
        className={`cursor-pointer select-none transition-colors hover:bg-muted/50 ${className} ${
          isActive ? 'font-semibold' : ''
        }`}
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center justify-between group">
          <span>{children}</span>
          <div className={`ml-1 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
          }`}>
            {isActive ? (
              isAsc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronUp className="h-3 w-3" />
            )}
          </div>
        </div>
      </TableHead>
    );
  };

  // Apply sorting to our filtered recipes (not store's getFilteredRecipes)
  const filteredRecipes = getSortedRecipes(recipes);

  // ADDED: Dashboard data fetching
  const fetchDashboardStats = useCallback(async () => {
    if (!isAdmin) return;
    
    setDashboardLoading(true);
    try {
      const stats = await getDashboardStats();
      setDashboardStats(stats);
    } catch {
    } finally {
      setDashboardLoading(false);
    }
  }, [isAdmin]);

  const fetchDashboardUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    setDashboardLoading(true);
    try {
      const { users, totalCount } = await getDashboardUsers(
        50, // pageSize
        (userCurrentPage - 1) * 50, // offset
        userSearchQuery,
        userRoleFilter,
        userSubscriptionFilter,
        userActivityFilter
      );
      setDashboardUsers(users);
      setUserTotalCount(totalCount);
    } catch {
    } finally {
      setDashboardLoading(false);
    }
  }, [isAdmin, userCurrentPage, userSearchQuery, userRoleFilter, userSubscriptionFilter, userActivityFilter]);

  // ADDED: Load dashboard data on mount
  useEffect(() => {
    if (isAdmin && dashboardView === 'dashboard') {
      fetchDashboardStats();
    }
  }, [isAdmin, dashboardView, fetchDashboardStats]);

  useEffect(() => {
    if (isAdmin && dashboardView === 'users') {
      fetchDashboardUsers();
    }
  }, [isAdmin, dashboardView, fetchDashboardUsers]);

  // ADDED: Set initial dashboard tab from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'dashboard' || tab === 'users' || tab === 'recipes') {
      setDashboardView(tab);
    }
  }, [location.search]);

  const handleTabChange = (tab: 'dashboard' | 'users' | 'recipes') => {
    setDashboardView(tab);
    navigate(`/manage?tab=${tab}`, { replace: true });
  };

  // ADDED: Dashboard export function
  const handleExportUsers = async () => {
    try {
      const csvData = await exportUserData('csv', userSearchQuery, userRoleFilter, userSubscriptionFilter);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
    }
  };

    // Handler to open ManageUserDialog from EnhancedUserTable
  const handleManageUser = (user: DashboardUser) => {
    setLastDashboardView(dashboardView); // Save current tab before opening modal
    setSelectedUser(user);
    setManageUserDialogOpen(true);
  };

  // Handler to close ManageUserDialog and restore previous tab
  const handleCloseManageUserDialog = () => {
    setManageUserDialogOpen(false);
    setSelectedUser(null);
    setDashboardView(lastDashboardView); // Restore previous tab
  };

  // Handler to save user changes (calls admin_update_user SQL function via Supabase RPC)
  // Atomic single-call update: name, country, role, subscription, chefcoins_balance.
  // Falls back to the previous multi-write flow if the new RPC is not deployed yet.
  const handleSaveUser = async (fields: {
    name: string;
    country: string | null;
    role: 'admin' | 'reader' | 'guest';
    subscription: 'free' | 'standard' | 'premium' | 'banned';
    chefcoins_balance: number;
  }) => {
    if (!selectedUser) return;
    setManageUserLoading(true);
    try {
      const { error: rpcError } = await supabase.rpc('admin_update_user', {
        p_user_id: selectedUser.id,
        p_name: fields.name,
        p_country: fields.country,
        p_role: fields.role,
        p_subscription: fields.subscription,
        p_chefcoins_balance: fields.chefcoins_balance,
      });

      // If the new RPC isn't deployed yet, fall back to the legacy 3-write flow.
      if (rpcError && (rpcError.code === '42883' || /function .* does not exist/i.test(rpcError.message))) {
        const { error: profileError } = await supabase.rpc('admin_update_user_profile', {
          p_user_id: selectedUser.id,
          p_name: fields.name,
          p_country: fields.country,
          p_role: fields.role,
        });
        if (profileError) throw profileError;

        if (fields.subscription !== selectedUser.subscription) {
          const { error: subError } = await supabase
            .from('user_profiles')
            .update({ subscription: fields.subscription })
            .eq('id', selectedUser.id);
          if (subError) throw subError;
        }

        if (fields.chefcoins_balance !== selectedUser.chefcoins_balance) {
          const { error: coinErr } = await supabase.functions.invoke('update-chefcoins', {
            body: { user_id: selectedUser.id, new_balance: fields.chefcoins_balance },
          });
          if (coinErr) throw new Error(coinErr.message || 'Failed to update ChefCoin balance');
        }
      } else if (rpcError) {
        throw rpcError;
      }

      setManageUserDialogOpen(false);
      setSelectedUser(null);
      await fetchDashboardUsers(); // Refresh user list
    } catch (err) {
      if (err instanceof Error) {
        alert('Failed to update user: ' + err.message);
      } else {
        alert('Failed to update user: ' + String(err));
      }
    } finally {
      setManageUserLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setManageUserLoading(true);
    try {
      await deleteUser(selectedUser.id); // Secure deletion via Edge Function
      setManageUserDialogOpen(false);
      setSelectedUser(null);
      await fetchDashboardUsers();
    } catch (err) {
      if (err instanceof Error) {
        alert('Failed to delete user: ' + err.message);
      } else {
        alert('Failed to delete user: ' + String(err));
      }
    } finally {
      setManageUserLoading(false);
    }
  };

    // New handler for opening the batch import modal
  const handleOpenBatchImportModal = () => {
    // Instead of opening modal, route to /import
    navigate('/import');
  };

  // Add state for user filter panel visibility
  const [userMobileFiltersOpen, setUserMobileFiltersOpen] = useState(false);

  // --- Robust Back Button Logic ---
  // Handles: /manage, /manage?tab=users, /manage?tab=recipes, /manage?tab=dashboard
  // const handleBack = () => {
  //   const params = new URLSearchParams(location.search);
  //   const tab = params.get('tab');
  //   // If on a tab, go to /manage (default tab, no query)
  //   if (tab === 'users' || tab === 'recipes' || tab === 'dashboard') {
  //     navigate('/manage', { replace: true });
  //   } else {
  //     // If already on /manage (no tab), go home or previous page
  //     if (window.history.length > 1) {
  //       navigate(-1);
  //     } else {
  //       navigate('/');
  //     }
  //   }
  // };

  // Render access denied if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          Access Restricted
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          This page is only available to administrators and registered users.
        </p>
      </div>
    );
  }

  return (
    <div
      className="fixed top-16 left-0 right-0 bottom-0 bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
      style={{
        overflowY: 'auto',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE 10+
      }}
      /* Hide scrollbar for Chrome, Safari and Opera */
      /* ::-webkit-scrollbar { display: none; } */
    >
      <div className="min-h-full">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Header with Navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {isAdmin ? (
                    dashboardView === 'dashboard' ? 'Analytics Dashboard' :
                    dashboardView === 'users' ? 'User Management' :
                    'Recipe Management'
                  ) : 'My Recipes'}
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  {isAdmin ? (
                    dashboardView === 'dashboard' ? 'Monitor platform performance and user activity' :
                    dashboardView === 'users' ? 'Manage users, roles, and permissions' :
                    'Create, edit, and organize recipes'
                  ) : 'Manage your personal recipe collection'}
                </p>
              </div>
            </div>
          </div>

          {/* ADDED: Dashboard Navigation Tabs (Admin Only) */}
          {isAdmin && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8 justify-center md:justify-start">
                <button
                  onClick={() => handleTabChange('dashboard')}
                  className={`group flex flex-col items-center justify-center py-2 px-3 border-b-2 font-semibold text-base transition-all duration-150 focus:outline-none ${
                    dashboardView === 'dashboard'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/40 dark:bg-orange-900/20'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:border-orange-400 dark:hover:text-orange-300 dark:hover:border-orange-400'
                  }`}
                >
                  <LayoutDashboard className={`mb-0.5 h-7 w-7 transition-colors ${dashboardView === 'dashboard' ? 'text-orange-500 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-300'}`} />
                  <span className="text-xs md:text-sm font-medium mt-0.5">Dashboard</span>
                </button>
                <button
                  onClick={() => handleTabChange('users')}
                  className={`group flex flex-col items-center justify-center py-2 px-3 border-b-2 font-semibold text-base transition-all duration-150 focus:outline-none ${
                    dashboardView === 'users'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/40 dark:bg-orange-900/20'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:border-orange-400 dark:hover:text-orange-300 dark:hover:border-orange-400'
                  }`}
                >
                  <Users className={`mb-0.5 h-7 w-7 transition-colors ${dashboardView === 'users' ? 'text-orange-500 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-300'}`} />
                  <span className="text-xs md:text-sm font-medium mt-0.5">Users</span>
                </button>
                <button
                  onClick={() => handleTabChange('recipes')}
                  className={`group flex flex-col items-center justify-center py-2 px-3 border-b-2 font-semibold text-base transition-all duration-150 focus:outline-none ${
                    dashboardView === 'recipes'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/40 dark:bg-orange-900/20'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:border-orange-400 dark:hover:text-orange-300 dark:hover:border-orange-400'
                  }`}
                >
                  <BookOpen className={`mb-0.5 h-7 w-7 transition-colors ${dashboardView === 'recipes' ? 'text-orange-500 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-300'}`} />
                  <span className="text-xs md:text-sm font-medium mt-0.5">Recipes</span>
                </button>
                <button
                  onClick={() => navigate('/manage/llm')}
                  className="group flex flex-col items-center justify-center py-2 px-3 border-b-2 font-semibold text-base transition-all duration-150 focus:outline-none border-transparent text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:border-orange-400 dark:hover:text-orange-300 dark:hover:border-orange-400"
                >
                  <Cpu className="mb-0.5 h-7 w-7 transition-colors text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-300" />
                  <span className="text-xs md:text-sm font-medium mt-0.5">LLM Models</span>
                </button>
              </nav>
            </div>
          )}

          {/* ADDED: Dashboard Content */}
          {isAdmin && dashboardView === 'dashboard' && (
            <div className="mt-8">
              <DashboardOverview 
                stats={dashboardStats!} 
                isLoading={dashboardLoading || !dashboardStats} 
              />
            </div>
          )}

          {/* ADDED: Enhanced Users View */}
          {isAdmin && dashboardView === 'users' && (
            <div className="mt-8">
              <EnhancedUserTable
                users={dashboardUsers}
                totalCount={userTotalCount}
                currentPage={userCurrentPage}
                pageSize={50}
                isLoading={dashboardLoading}
                searchQuery={userSearchQuery}
                roleFilter={userRoleFilter}
                subscriptionFilter={userSubscriptionFilter}
                activityFilter={userActivityFilter}
                onSearchChange={setUserSearchQuery}
                onRoleFilterChange={setUserRoleFilter}
                onSubscriptionFilterChange={setUserSubscriptionFilter}
                onActivityFilterChange={setUserActivityFilter}
                onPageChange={setUserCurrentPage}
                onExport={handleExportUsers}
                onManageUser={handleManageUser}
                mobileFiltersOpen={userMobileFiltersOpen}
                setMobileFiltersOpen={setUserMobileFiltersOpen}
              />
              <ManageUserDialog
                user={selectedUser}
                open={manageUserDialogOpen}
                onClose={handleCloseManageUserDialog}
                onSave={handleSaveUser}
                onDelete={handleDeleteUser}
                loading={manageUserLoading}
              />
            </div>
          )}

          {/* ADDED: Show existing user management for readers OR admin recipe view */}
          {(dashboardView === 'recipes' || !isAdmin) && (
            <div className="mt-8">
              {/* Show User Management for readers, or keep recipe management for admins */}
              {showUserManagement ? (
                <UserManagementV2 
                  isOpen={showUserManagement} 
                  onOpenChange={setShowUserManagement} 
                />
              ) : (
                <div className="space-y-6">
                  {/* Mobile Filter Toggle - moved to top level */}
                  <div className="sm:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsMobileFiltersVisible(!isMobileFiltersVisible)}
                      aria-label="Toggle Filters"
                      className="relative flex items-center gap-2 w-full justify-center"
                    >
                     { (filters.searchQuery || filters.category !== 'all' || filters.showFavoritesOnly || filters.showOwnRecipesOnly) && (
                       <span className="absolute -top-1 -right-1 block h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-background" />
                     )}
                      <Filter className="h-4 w-4" />
                      Filters
                      <ChevronDown className={`h-4 w-4 transition-transform ${isMobileFiltersVisible ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {/* Mobile Filter Panel - Inline */}
                    {isMobileFiltersVisible && (
                      <div className="mt-3 p-4 border rounded-lg bg-background shadow-sm">
                        <RecipeFilterToolbar
                          filters={filters}
                          pagination={pagination}
                          loading={loading}
                          selectedCount={selectedRecipes.length}
                          onSearchChange={setSearchQuery}
                          onCategoryChange={setCategory}
                          onFavoritesToggle={setShowFavoritesOnly}
                          onShowOwnRecipesOnlyToggle={setShowOwnRecipesOnly}
                          onLimitChange={setLimit}
                          onNextPage={nextPage}
                          onPreviousPage={previousPage}
                          onExportSelected={handleExportSelected}
                          onExportAll={handleExportAll}
                          onImport={handleOpenBatchImportModal}
                          isExporting={loadingAllRecipes}
                          showActions={false}
                          showStats={false}
                          variant="manage"
                        />
                      </div>
                    )}
                  </div>

                  {/* Recipe Management Section */}
                  <div className="mt-4">

                    {/* Desktop Filter Toolbar */}
                    {!isMobileView && (
                      <RecipeFilterToolbar
                        filters={filters}
                        pagination={pagination}
                        loading={loading}
                        selectedCount={selectedRecipes.length}
                        onSearchChange={setSearchQuery}
                        onCategoryChange={setCategory}
                        onFavoritesToggle={setShowFavoritesOnly}
                        onShowOwnRecipesOnlyToggle={setShowOwnRecipesOnly}
                        onLimitChange={setLimit}
                        onNextPage={nextPage}
                        onPreviousPage={previousPage}
                        onExportSelected={handleExportSelected}
                        onExportAll={handleExportAll}
                        onImport={handleOpenBatchImportModal}
                        isExporting={loadingAllRecipes}
                        showActions={true}
                        showStats={false}
                        variant="manage"
                      />
                    )}

                    {/* Loading, Error, Empty States remain unchanged */}

                    {/* Desktop Table */}
                    <div className="hidden md:block rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50 dark:bg-muted/75">
                            <TableHead className="w-[32px]">
                              <Checkbox
                                checked={selectedRecipes.length === filteredRecipes.length && filteredRecipes.length > 0}
                                onCheckedChange={handleSelectAll}
                                className="h-3.5 w-3.5"
                              />
                            </TableHead>
                            <SortableHeader sortKey="title">Recipe Name</SortableHeader>
                            <SortableHeader sortKey="category">Category</SortableHeader>
                            <SortableHeader sortKey="dateCreated">Date Added</SortableHeader>
                            <SortableHeader sortKey="isFavorite" className="text-center">Favorite</SortableHeader>
                            <SortableHeader sortKey="share" className="text-center">Share</SortableHeader>
                            <TableHead className="w-[80px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredRecipes.map((recipe) => (
                            <TableRow
                              key={recipe.id}
                              className="cursor-pointer hover:bg-muted/30 transition-colors"
                              onClick={() => handleRecipeClick(recipe)}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedRecipes.includes(recipe.id)}
                                  onCheckedChange={(checked) => handleSelectRecipe(recipe.id, checked as boolean)}
                                  className="h-3.5 w-3.5"
                                />
                              </TableCell>
                              <TableCell className="font-medium">{recipe.title}</TableCell>
                              <TableCell>{recipe.category || 'Uncategorized'}</TableCell>
                              <TableCell>{formatDate(recipe.dateCreated)}</TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-center">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleFavoriteClick(recipe)}
                                    disabled={favoriteLoading === recipe.id}
                                  >
                                    <Star
                                      className={`h-3.5 w-3.5 ${
                                        recipe.isFavorite
                                          ? 'fill-yellow-400 text-yellow-400'
                                          : 'text-gray-300 hover:text-gray-400'
                                      } ${favoriteLoading === recipe.id ? 'animate-pulse' : ''}`}
                                    />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-center">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleShareToggle(recipe)}
                                    disabled={shareLoading === recipe.id}
                                  >
                                    <div className={`rounded-full p-1 transition-colors ${
                                      recipe.share
                                        ? 'bg-blue-500/90 text-white'
                                        : 'bg-red-500/90 text-white'
                                    } ${shareLoading === recipe.id ? 'animate-pulse' : ''}`}>
                                      {recipe.share ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                    </div>
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => onEdit(recipe)}
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => onDelete(recipe.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3 sm:space-y-4">
                      {filteredRecipes.map((recipe) => (
                        <div
                          key={recipe.id}
                          className="p-3 sm:p-4 border rounded-lg space-y-3 bg-background shadow-sm safe-area-inset cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => handleRecipeClick(recipe)}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-base truncate pr-2 flex-1">{recipe.title}</h3>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 ml-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFavoriteClick(recipe);
                              }}
                              disabled={favoriteLoading === recipe.id}
                              aria-label={recipe.isFavorite ? 'Unfavorite' : 'Favorite'}
                            >
                              <Star
                                className={`h-5 w-5 ${
                                  recipe.isFavorite
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300 hover:text-gray-400'
                                } ${favoriteLoading === recipe.id ? 'animate-pulse' : ''}`}
                              />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="inline-block px-2 py-0.5 bg-muted rounded-full">{recipe.category || 'Uncategorized'}</span>
                            <span className="inline-block px-2 py-0.5 bg-muted rounded-full">Added: {formatDate(recipe.dateCreated)}</span>
                            <span className={`inline-block px-2 py-0.5 rounded-full ${recipe.share ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{recipe.share ? 'Shared' : 'Admin only'}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(recipe);
                              }}
                              aria-label="Edit recipe"
                            >
                              <Edit2 className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(recipe.id);
                              }}
                              aria-label="Delete recipe"
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShareToggle(recipe);
                              }}
                              disabled={shareLoading === recipe.id}
                              aria-label={recipe.share ? 'Unshare' : 'Share'}
                            >
                              <div className={`rounded-full p-1 transition-colors ${
                                recipe.share
                                  ? 'bg-blue-500/90 text-white'
                                  : 'bg-red-500/90 text-white'
                              } ${shareLoading === recipe.id ? 'animate-pulse' : ''}`}>
                                {recipe.share ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                              </div>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            selectedRecipes={
              exportMode === 'selected'
                ? filteredRecipes.filter(recipe => selectedRecipes.includes(recipe.id))
                : allRecipesForExport
            }
          />
        </div>
      </div>
    </div>
  );
}
